import { useCallback, useMemo, useState } from 'react';
import images from '@config/images';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import { Button, FlexView, Image, Text } from '@goodfynd/ui';
import { useAlertDialog } from '@goodfynd/ui';
import { getDashboardRoutePath } from '@utils/routes-util';

import type {
  OnboardingFeature,
  OnboardingTask,
} from '../components/onboarding-widget';
import {
  OnboardingTaskId,
  type OnboardingTaskStatusMap,
} from '../components/onboarding-widget/types';

import useConnectStripe from './useConnectStripe';
import { useOnboardingChecks } from './useOnboardingChecks';

const routes = config.routes;

export const useOnboardingTasks = () => {
  const { vendor } = useVendor();
  const vendorNameId = vendor?.nameId;

  const openAlert = useAlertDialog();
  const [taskStatuses, setTaskStatuses] = useState<OnboardingTaskStatusMap>({
    [OnboardingTaskId.CateringLeadPinned]: 'hidden',
    [OnboardingTaskId.CateringLeads]: 'hidden',
    [OnboardingTaskId.CateringLeadsGenerating]: 'hidden',
    [OnboardingTaskId.CreateCatalog]: 'hidden',
    [OnboardingTaskId.CreateInvoice]: 'hidden',
    [OnboardingTaskId.CreateTransaction]: 'hidden',
    [OnboardingTaskId.DownloadApp]: 'hidden',
    [OnboardingTaskId.EventLeads]: 'hidden',
    [OnboardingTaskId.EventLeadsGenerating]: 'hidden',
    [OnboardingTaskId.EventLeadPinned]: 'hidden',
    [OnboardingTaskId.OnlineOrdering]: 'hidden',
    [OnboardingTaskId.Pos]: 'hidden',
    [OnboardingTaskId.Quotes]: 'hidden',
    [OnboardingTaskId.ReviewInvoiceReports]: 'hidden',
    [OnboardingTaskId.ReviewTransactionReports]: 'hidden',
    [OnboardingTaskId.SetSchedule]: 'hidden',
    [OnboardingTaskId.SetTaxSettings]: 'hidden',
    [OnboardingTaskId.StripeConnect]: 'hidden',
    [OnboardingTaskId.VenueLeads]: 'hidden',
    [OnboardingTaskId.VenueLeadsGenerating]: 'hidden',
    [OnboardingTaskId.VenueLeadPinned]: 'hidden',
    [OnboardingTaskId.Website]: 'hidden',
    [OnboardingTaskId.WebsiteDefaultMenu]: 'hidden',
    [OnboardingTaskId.WebsiteLogo]: 'hidden',
    [OnboardingTaskId.WebsitePublish]: 'hidden',
  });
  const { isLoading } = useOnboardingChecks(setTaskStatuses);

  const { busy: connectStripeBusy, connectStripe } = useConnectStripe();

  const handleDownloadApp = useCallback(() => {
    openAlert({
      title: 'Download Point of Sale App',
      message: (
        <FlexView direction="column" gap={16} margin="16px 0 0" width="100%">
          <Button fullWidth href={config.app.apps.pos.ios} rank="secondary">
            <FlexView align="center" gap={8}>
              <Image
                alt="iOS logo"
                height={16}
                src={images.icons.ios}
                width={16}
              />
              <Text as="body2" inherit>
                Download for iOS
              </Text>
            </FlexView>
          </Button>

          <Button fullWidth href={config.app.apps.pos.android} rank="secondary">
            <FlexView align="center" gap={8}>
              <Image
                alt="Android logo"
                height={16}
                src={images.icons.android}
                width={16}
              />
              <Text as="body2" inherit>
                Download for Android
              </Text>
            </FlexView>
          </Button>
        </FlexView>
      ),
    });
  }, [openAlert]);

  const profileTasks = useMemo<OnboardingTask[]>(() => {
    return [
      {
        href: getDashboardRoutePath(
          routes.dashboard.products.menus,
          vendorNameId
        ),
        id: OnboardingTaskId.CreateCatalog,
        isComplete:
          taskStatuses[OnboardingTaskId.CreateCatalog] === 'completed',
        label: 'Create catalog',
      },
      {
        href: getDashboardRoutePath(
          routes.dashboard.schedule.home,
          vendorNameId
        ),
        id: OnboardingTaskId.SetSchedule,
        isComplete: taskStatuses[OnboardingTaskId.SetSchedule] === 'completed',
        label: 'Set schedule',
      },
    ];
  }, [taskStatuses, vendorNameId]);

  const orderingTasks = useMemo<OnboardingTask[]>(() => {
    return [
      ...profileTasks,
      {
        href: getDashboardRoutePath(
          routes.dashboard.transactions.walkups,
          vendorNameId
        ),
        id: OnboardingTaskId.CreateTransaction,
        isComplete:
          taskStatuses[OnboardingTaskId.CreateTransaction] === 'completed',
        label: 'Process first transaction',
      },
      {
        hasOptions: true,
        href: getDashboardRoutePath(
          routes.dashboard.reports.transactions.tickets,
          vendorNameId
        ),
        id: OnboardingTaskId.ReviewTransactionReports,
        label: 'Review transaction reports',
        options: [
          {
            label: 'Review transaction reports',
            value: OnboardingTaskId.ReviewTransactionReports,
          },
        ],
      },
    ];
  }, [profileTasks, taskStatuses, vendorNameId]);

  const paymentsTasks = useMemo<OnboardingTask[]>(() => {
    return [
      {
        busy: connectStripeBusy,
        id: OnboardingTaskId.StripeConnect,
        isComplete:
          taskStatuses[OnboardingTaskId.StripeConnect] === 'completed',
        label: 'Create payments account',
        onClick: () => {
          if (taskStatuses[OnboardingTaskId.StripeConnect] === 'hidden') {
            connectStripe();
            return;
          }

          if (taskStatuses[OnboardingTaskId.StripeConnect] === 'pending') {
            openAlert({
              title: 'Verifying your payments account',
              message: 'Please wait while we verify your payments account.',
            });
            return;
          }

          openAlert({
            title: 'Congratulations!',
            message: 'You are ready to accept payments.',
          });
        },
      },
      {
        href: `${getDashboardRoutePath(
          routes.dashboard.settings.ordering,
          vendorNameId
        )}?orgStep=3`,
        id: OnboardingTaskId.SetTaxSettings,
        isComplete:
          taskStatuses[OnboardingTaskId.SetTaxSettings] === 'completed',
        label: 'Set tax settings',
      },
    ];
  }, [connectStripe, connectStripeBusy, openAlert, taskStatuses, vendorNameId]);

  const features = useMemo<OnboardingFeature[]>(() => {
    return [
      {
        hide: taskStatuses[OnboardingTaskId.CateringLeads] === 'hidden',
        id: OnboardingTaskId.CateringLeads,
        title: 'FyndAI Catering Leads',
        tasks: [
          {
            hide:
              taskStatuses[OnboardingTaskId.CateringLeadsGenerating] ===
              'hidden',
            id: OnboardingTaskId.CateringLeadsGenerating,
            isComplete:
              taskStatuses[OnboardingTaskId.CateringLeadsGenerating] ===
              'completed',
            label: 'Generating catering leads',
            href: getDashboardRoutePath(
              routes.dashboard.events.cateringLeads,
              vendorNameId
            ),
          },
          {
            hasOptions: true,
            href: getDashboardRoutePath(
              routes.dashboard.events.cateringLeads,
              vendorNameId
            ),
            id: OnboardingTaskId.CateringLeadPinned,
            isComplete:
              taskStatuses[OnboardingTaskId.CateringLeadPinned] === 'completed',
            label: 'Pin a catering lead',
          },
        ],
      },
      {
        hide: taskStatuses[OnboardingTaskId.EventLeads] === 'hidden',
        id: OnboardingTaskId.EventLeads,
        title: 'FyndAI Event Leads',
        tasks: [
          {
            hide:
              taskStatuses[OnboardingTaskId.EventLeadsGenerating] === 'hidden',
            id: OnboardingTaskId.EventLeadsGenerating,
            isComplete:
              taskStatuses[OnboardingTaskId.EventLeadsGenerating] ===
              'completed',
            label: 'Generating event leads',
            href: getDashboardRoutePath(
              routes.dashboard.events.leads,
              vendorNameId
            ),
          },
          {
            hasOptions: true,
            href: getDashboardRoutePath(
              routes.dashboard.events.leads,
              vendorNameId
            ),
            id: OnboardingTaskId.EventLeadPinned,
            isComplete:
              taskStatuses[OnboardingTaskId.EventLeadPinned] === 'completed',
            label: 'Pin an event lead',
          },
        ],
      },
      {
        hide: taskStatuses[OnboardingTaskId.VenueLeads] === 'hidden',
        id: OnboardingTaskId.VenueLeads,
        title: 'FyndAI Venue Leads',
        tasks: [
          {
            hide:
              taskStatuses[OnboardingTaskId.VenueLeadsGenerating] === 'hidden',
            id: OnboardingTaskId.VenueLeadsGenerating,
            isComplete:
              taskStatuses[OnboardingTaskId.VenueLeadsGenerating] ===
              'completed',
            label: 'Generating venue leads',
            href: getDashboardRoutePath(
              routes.dashboard.events.venueLeads,
              vendorNameId
            ),
          },
          {
            hasOptions: true,
            href: getDashboardRoutePath(
              routes.dashboard.events.venueLeads,
              vendorNameId
            ),
            id: OnboardingTaskId.VenueLeadPinned,
            isComplete:
              taskStatuses[OnboardingTaskId.VenueLeadPinned] === 'completed',
            label: 'Pin a venue lead',
          },
        ],
      },
      {
        busy: connectStripeBusy,
        hide: taskStatuses[OnboardingTaskId.Pos] === 'hidden',
        id: OnboardingTaskId.Pos,
        title: 'Point of Sale',
        tasks: [
          ...paymentsTasks,
          ...orderingTasks,
          {
            hasOptions: true,
            id: OnboardingTaskId.DownloadApp,
            label: 'Download the Point of Sale app',
            onClick: handleDownloadApp,
            isComplete:
              taskStatuses[OnboardingTaskId.DownloadApp] === 'completed',
          },
        ],
      },
      {
        busy: connectStripeBusy,
        hide: taskStatuses[OnboardingTaskId.OnlineOrdering] === 'hidden',
        id: OnboardingTaskId.OnlineOrdering,
        title: 'Online Ordering',
        tasks: [...paymentsTasks, ...orderingTasks],
      },
      {
        busy: connectStripeBusy,
        hide: taskStatuses[OnboardingTaskId.Quotes] === 'hidden',
        id: OnboardingTaskId.Quotes,
        title: 'Invoices & Quotes',
        tasks: [
          ...paymentsTasks,
          {
            href: getDashboardRoutePath(
              routes.dashboard.invoicing.invoices,
              vendorNameId
            ),
            id: OnboardingTaskId.CreateInvoice,
            isComplete:
              taskStatuses[OnboardingTaskId.CreateInvoice] === 'completed',
            label: 'Create first invoice or quote',
          },
          {
            hasOptions: true,
            href: getDashboardRoutePath(
              routes.dashboard.reports.transactions.invoices,
              vendorNameId
            ),
            id: OnboardingTaskId.ReviewInvoiceReports,
            label: 'Review invoice reports',
            options: [
              {
                label: 'Review invoice reports',
                value: OnboardingTaskId.ReviewInvoiceReports,
              },
            ],
          },
        ],
      },
      {
        busy: connectStripeBusy,
        hide: taskStatuses[OnboardingTaskId.Website] === 'hidden',
        id: OnboardingTaskId.Website,
        title: 'Instant Website',
        tasks: [
          {
            href: getDashboardRoutePath(
              routes.dashboard.settings.website,
              vendorNameId
            ),
            id: OnboardingTaskId.WebsiteLogo,
            isComplete:
              taskStatuses[OnboardingTaskId.WebsiteLogo] === 'completed',
            label: 'Upload website logo',
          },
          ...profileTasks,
          {
            href: getDashboardRoutePath(
              routes.dashboard.settings.website,
              vendorNameId
            ),
            id: OnboardingTaskId.WebsiteDefaultMenu,
            isComplete:
              taskStatuses[OnboardingTaskId.WebsiteDefaultMenu] === 'completed',
            label: 'Set website default menu',
          },
          {
            href: getDashboardRoutePath(
              routes.dashboard.settings.website,
              vendorNameId
            ),
            id: OnboardingTaskId.WebsitePublish,
            isComplete:
              taskStatuses[OnboardingTaskId.WebsitePublish] === 'completed',
            label: 'Publish website',
          },
        ],
      },
    ];
  }, [
    connectStripeBusy,
    handleDownloadApp,
    orderingTasks,
    paymentsTasks,
    profileTasks,
    taskStatuses,
    vendorNameId,
  ]);

  return {
    features: features
      .filter((feature) => !feature.hide)
      .sort((a, b) => a.title.localeCompare(b.title)),
    isLoading,
  };
};
